/**
 * Rewrite directives (ENGINE-SPEC §9.3): the seed's INV-DIR-01…23 verbatim (data: `lang/en/lexicons/seed-2.6/build-directives.json`)
 * plus the process-channel directives of `lang/en/v3/directives.v3.json` (C-DIR-NEW).
 *
 * Firing: a channel fires when its score is ≥ 50 and its κ is > 0.2, on the corrected v3 values (C-DIR-FIRE). Order: the seed's
 * directives in seed order, then the new ones in table order, then the always-on rules (INV-DIR-21…23).
 *
 * No directive exists for FM-01, PT-02, SF-01, SF-03, PROC-02, SG-01 (and SR-01, which the seed has none for either). The seed's
 * INV-DIR-19 is keyed on SG-01; ENGINE-SPEC §9.3 lists SG-01 as directive-free, so it is not emitted (reported in
 * docs/reports/bench-notes.md).
 */
import type { CoreAnalysis } from '../analyse.ts';
import type { ChannelId } from '../channels/types.ts';
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import directivesV3 from '../lang/en/v3/directives.v3.json' with { type: 'json' };

export const FIRE_MIN_SCORE = 50;
/** Fires when κ is strictly above this. */
export const FIRE_MIN_KAPPA = 0.2;

/** Channels that never get a directive. */
export const DIRECTIVE_FREE_CHANNELS: ReadonlySet<string> = new Set([
	'FM-01',
	'PT-02',
	'SF-01',
	'SF-03',
	'SR-01',
	'PROC-02',
	'SG-01',
]);

export interface DirectiveEvidence {
	/** Evidence labels of the LX channels (INV-DIR-01's `{vocab}`). */
	vocab?: readonly string[];
	/** PROC-01's most repeated phrases. */
	refrains?: readonly string[];
	/** PROC-08's most productive frames. */
	frames?: readonly string[];
}

interface V3Entry {
	when: string[];
	text: string;
	slot?: 'refrains' | 'frames';
	suffix?: string;
}

/** The directive texts of the seed (23) and of the v3 additions (7), as data. */
export function directiveData() {
	const seed = loadEnglishLexicons().buildDirectives;
	return {
		seed,
		v3: directivesV3 as unknown as { entries: V3Entry[]; slotLimit: number; slotSeparator: string },
	};
}

/** The channels that have a directive (seed or new). */
export function directiveChannels(): Set<ChannelId> {
	const { seed, v3 } = directiveData();
	const out = new Set<ChannelId>();
	for (const e of [...seed.entries, ...v3.entries])
		for (const c of e.when) if (!DIRECTIVE_FREE_CHANNELS.has(c)) out.add(c as ChannelId);
	return out;
}

/** The seed's `buildDirectives` on a set of fired channel ids, with the v3 additions. */
export function buildDirectives(
	fired: ReadonlySet<string> | readonly string[],
	evidence: DirectiveEvidence = {}
): string[] {
	const set: ReadonlySet<string> =
		fired instanceof Set ? fired : new Set(fired as readonly string[]);
	const has = (c: string) => set.has(c) && !DIRECTIVE_FREE_CHANNELS.has(c);
	const { seed, v3 } = directiveData();
	const out: string[] = [];
	const always: string[] = [];
	for (const e of seed.entries) {
		if (e.when.length === 0) {
			always.push(e.text);
			continue;
		}
		if (!e.when.some(has)) continue;
		if (e.text.includes('{vocab}')) {
			const vocab = [...new Set(evidence.vocab ?? [])].slice(0, seed.vocabLimit);
			out.push(e.text.replace('{vocab}', vocab.join(seed.vocabSeparator)));
		} else out.push(e.text);
	}
	for (const e of v3.entries) {
		if (!e.when.some(has)) continue;
		let text = e.text;
		if (e.slot && e.suffix) {
			const items = [...new Set(evidence[e.slot] ?? [])].slice(0, v3.slotLimit);
			if (items.length) text += e.suffix.replace(`{${e.slot}}`, items.join(v3.slotSeparator));
		}
		out.push(text);
	}
	return [...out, ...always];
}

/** Channels that fire on the document: score ≥ 50 and κ > 0.2 (scored channels only). */
export function firedChannels(core: CoreAnalysis): ChannelId[] {
	const out: ChannelId[] = [];
	for (const r of core.results) {
		if (r.status !== 'scored') continue;
		if (r.doc.score >= FIRE_MIN_SCORE && r.doc.kappa > FIRE_MIN_KAPPA) out.push(r.id);
	}
	return out;
}

const QUOTES = /[“”]/g;

function labelOf(summaryLine: string): string {
	const i = summaryLine.indexOf(' — ');
	return (i >= 0 ? summaryLine.slice(0, i) : summaryLine).replace(QUOTES, '').trim();
}

const quotedIn = (line: string): string | null => /"([^"]+)"/.exec(line)?.[1] ?? null;

/** Evidence for the placeholders: LX labels, PROC-01 refrains, PROC-08 frames (from the channels' summary lines). */
export function directiveEvidence(core: CoreAnalysis): DirectiveEvidence {
	const vocab: string[] = [];
	for (const r of core.results) {
		// LX-04 (REFIT-S) reports a level (MATTR) and its paragraphs, not words to purge
		if (!r.id.startsWith('LX') || r.id === 'LX-04') continue;
		for (const line of r.summary) {
			// LX-01's first line describes the intensifier-adverb saturation, not a word to purge
			if (/^\d+ intensifier adverbs/.test(line)) continue;
			const l = labelOf(line);
			if (l) vocab.push(l);
		}
	}
	const pick = (id: ChannelId): string[] =>
		(core.byId[id]?.summary ?? []).map(quotedIn).filter((x): x is string => x !== null);
	return {
		vocab: [...new Set(vocab)].slice(0, 25),
		refrains: pick('PROC-01'),
		frames: pick('PROC-08'),
	};
}
